import sanitizeHtml from 'sanitize-html';

// Colour values the admin rich text editor (TGTextEditor / draftToHtml) emits:
// #rgb/#rgba/#rrggbb/#rrggbbaa, rgb()/rgba() with 0-255 channels, hsl()/hsla()
// with a 0-360 hue and 0-100% saturation/lightness, and CSS colour keywords.
const RGB_CHANNEL = '(?:25[0-5]|2[0-4]\\d|1\\d{2}|[1-9]?\\d)';
const ALPHA = '(?:0|1|0?\\.\\d+)';
const HUE = '(?:360|3[0-5]\\d|[12]\\d{2}|[1-9]?\\d)';
const PERCENT = '(?:100|[1-9]?\\d)%';
const COLOR_VALUES = [
  /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i,
  new RegExp(
    `^rgb\\(\\s*${RGB_CHANNEL}\\s*,\\s*${RGB_CHANNEL}\\s*,\\s*${RGB_CHANNEL}\\s*\\)$`,
    'i'
  ),
  new RegExp(
    `^rgba\\(\\s*${RGB_CHANNEL}\\s*,\\s*${RGB_CHANNEL}\\s*,\\s*${RGB_CHANNEL}\\s*,\\s*${ALPHA}\\s*\\)$`,
    'i'
  ),
  new RegExp(
    `^hsl\\(\\s*${HUE}\\s*,\\s*${PERCENT}\\s*,\\s*${PERCENT}\\s*\\)$`,
    'i'
  ),
  new RegExp(
    `^hsla\\(\\s*${HUE}\\s*,\\s*${PERCENT}\\s*,\\s*${PERCENT}\\s*,\\s*${ALPHA}\\s*\\)$`,
    'i'
  ),
  // CSS colour keywords (red, darkslategray, transparent...) — letters only, so
  // no url()/expression()/var() can ever match.
  /^[a-z]{3,20}$/i,
];

// Inline styles the editor can emit on note content. Whitelisting specific
// properties (with value validation) lets rich formatting through while still
// blocking dangerous constructs like url()/expression().
const SANITIZE_OPTIONS = {
  allowedTags: [
    'a',
    'b',
    'blockquote',
    'br',
    'code',
    'del',
    'div',
    'em',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'hr',
    'i',
    'ins',
    'li',
    'ol',
    'p',
    'pre',
    's',
    'span',
    'strike',
    'strong',
    'sub',
    'sup',
    'u',
    'ul',
  ],
  allowedAttributes: {
    a: ['href', 'target', 'rel'],
    '*': ['class', 'style'],
  },
  allowedStyles: {
    '*': {
      color: COLOR_VALUES,
      'background-color': COLOR_VALUES,
      'font-size': [/^\d+(?:\.\d+)?(?:px|em|rem|%|pt)$/],
      // Comma-separated stack where each item is either a fully-quoted string
      // (matched quotes) or bare word characters — rejects stray/mismatched
      // quotes, dangling commas, and CSS-parsing edge-case payloads while
      // still accepting mixed stacks like "Helvetica Neue", Arial, sans-serif
      'font-family': [
        /^(?:"[\w\s-]+"|'[\w\s-]+'|[\w-]+(?:\s+[\w-]+)*)(?:\s*,\s*(?:"[\w\s-]+"|'[\w\s-]+'|[\w-]+(?:\s+[\w-]+)*))*$/,
      ],
      'font-weight': [/^(?:normal|bold|bolder|lighter|[1-9]00)$/],
      'font-style': [/^(?:normal|italic|oblique)$/],
      'text-align': [/^(?:left|right|center|justify)$/],
      'text-decoration': [
        /^(?:none|underline|line-through|overline)(?:\s+\w+)*$/,
      ],
    },
  },
  allowedSchemes: ['http', 'https', 'mailto', 'tel'],
  disallowedTagsMode: 'discard',
  allowedSchemesByTag: {},
  allowProtocolRelative: false,
  enforceHtmlBoundary: true,
};

export const sanitizedData = (data) => ({
  __html: sanitizeHtml(data || '', SANITIZE_OPTIONS),
});

export default sanitizedData;
